import { Link } from "react-router-dom";
import { ChevronRight, Home as HomeIcon } from "lucide-react";
import { Helmet } from "react-helmet-async";
import { SITE_URL } from "@/components/SEO";

/**
 * Breadcrumbs — renders visible nav + Schema.org BreadcrumbList JSON-LD.
 * items: [{ name, path }] — path optional for the last (current) item.
 *
 * Emits Schema.org-valid BreadcrumbList: every ListItem has BOTH `name`
 * AND `item` populated. Google Search Console's Rich Results validator
 * fails a page if any breadcrumb is missing either field, so for the last
 * (current) item we synthesize `item` from the current URL.
 */
export default function Breadcrumbs({ items = [] }) {
  // Defensive: coerce legacy {label, to} callers into the canonical shape
  // so a stray page can't silently break structured data.
  const normalized = items
    .map((it) => ({
      name: it?.name || it?.label,
      path: it?.path || it?.to,
    }))
    .filter((it) => Boolean(it.name));

  const all = [{ name: "Home", path: "/" }, ...normalized];

  // Resolve the current page URL for the trailing crumb (Google needs `item`).
  const currentPath =
    typeof window !== "undefined" ? window.location.pathname : "/";

  const schema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: all.map((it, idx) => {
      const isLast = idx === all.length - 1;
      const path = it.path || (isLast ? currentPath : "/");
      return {
        "@type": "ListItem",
        position: idx + 1,
        name: it.name,
        item: `${SITE_URL}${path}`,
      };
    }),
  };
  return (
    <>
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(schema)}</script>
      </Helmet>
      <nav
        aria-label="Breadcrumb"
        className="flex items-center flex-wrap gap-1 text-xs text-zinc-500 mb-4"
        data-testid="breadcrumbs"
      >
        {all.map((it, idx) => {
          const isLast = idx === all.length - 1;
          return (
            <span key={idx} className="inline-flex items-center gap-1">
              {idx === 0 ? <HomeIcon className="w-3 h-3" /> : null}
              {it.path && !isLast ? (
                <Link to={it.path} className="hover:text-white transition-colors">
                  {it.name}
                </Link>
              ) : (
                <span className={isLast ? "text-zinc-300" : ""}>{it.name}</span>
              )}
              {!isLast ? <ChevronRight className="w-3 h-3 text-zinc-600" /> : null}
            </span>
          );
        })}
      </nav>
    </>
  );
}
